/**
 * RecipePicker Modal Component
 *
 * A modal dialog for selecting recipes to assign to meal slots.
 * Displays a searchable list of recipes with real-time filtering.
 * Allows users to select a recipe and specify servings for the meal slot.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecipeStore } from '../../stores/recipeStore';
import { useMealPlanStore } from '../../stores/mealPlanStore';
import type { Recipe } from '../../types/recipe';
import type { MealType, DayOfWeek, MealSlot } from '../../types/mealPlan';
import './mealPlan.css';

interface RecipePickerProps {
  isOpen: boolean;
  onClose: () => void;
  dayIndex: DayOfWeek;
  mealType: MealType;
  currentSlot: MealSlot | null;
  mealPlanId: number;
}

export function RecipePicker({
  isOpen,
  onClose,
  dayIndex,
  mealType,
  currentSlot,
  mealPlanId,
}: RecipePickerProps) {
  const { t } = useTranslation(['mealPlan', 'common', 'recipes']);
  const navigate = useNavigate();
  const { recipes, fetchRecipes, isLoading: recipesLoading } = useRecipeStore();
  const { assignRecipeToSlot, updateMealSlot, removeMealSlot } = useMealPlanStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [servings, setServings] = useState<number>(4);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch recipes on mount
  useEffect(() => {
    if (isOpen) {
      fetchRecipes();
    }
  }, [isOpen, fetchRecipes]);

  // Initialize servings from current slot or selected recipe
  useEffect(() => {
    if (currentSlot?.servings) {
      setServings(currentSlot.servings);
    } else if (selectedRecipe?.servings) {
      setServings(selectedRecipe.servings);
    }
  }, [currentSlot, selectedRecipe]);

  // Filter recipes based on search query
  const filteredRecipes = useMemo(() => {
    if (!searchQuery.trim()) {
      return recipes;
    }

    const query = searchQuery.toLowerCase();
    return recipes.filter((recipe) => {
      const titleMatch = recipe.title.toLowerCase().includes(query);
      const descriptionMatch = recipe.description?.toLowerCase().includes(query) || false;
      const ingredientsMatch = recipe.ingredients?.some((ingredient) =>
        ingredient.name.toLowerCase().includes(query)
      ) || false;

      return titleMatch || descriptionMatch || ingredientsMatch;
    });
  }, [recipes, searchQuery]);

  // Handle recipe selection
  const handleRecipeSelect = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setServings(recipe.servings);
  };

  // Handle assigning/updating recipe to slot
  const handleAssignRecipe = async () => {
    if (!selectedRecipe) return;

    setIsSubmitting(true);
    try {
      if (currentSlot) {
        // Update existing slot
        await updateMealSlot(mealPlanId, currentSlot.id, {
          recipe_id: selectedRecipe.id,
          servings,
        });
      } else {
        // Create new slot
        await assignRecipeToSlot(
          mealPlanId,
          dayIndex,
          mealType,
          selectedRecipe.id,
          servings
        );
      }

      onClose();
    } catch (error) {
      // Error handling is done in the store
      console.error('Failed to assign recipe:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle removing recipe from slot
  const handleRemoveRecipe = async () => {
    if (!currentSlot) return;

    setIsSubmitting(true);
    try {
      await removeMealSlot(mealPlanId, currentSlot.id);
      onClose();
    } catch (error) {
      console.error('Failed to remove recipe:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setSearchQuery('');
      setSelectedRecipe(null);
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const dayName = t(`mealPlan:${DAY_KEYS[dayIndex]}`);
  const mealLabel = t(`mealPlan:${mealType}`);

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="recipe-picker-title"
    >
      <div
        className="modal-content recipe-picker-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 id="recipe-picker-title">{t('mealPlan:selectRecipe')}</h2>
            <p className="modal-subtitle">
              {dayName} - {mealLabel}
            </p>
          </div>
          <button
            className="modal-close-button"
            onClick={handleClose}
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Search input */}
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder={t('mealPlan:searchRecipes')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={recipesLoading || isSubmitting}
              aria-label="Search recipes"
            />
            {searchQuery && (
              <button
                className="clear-search-button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                disabled={isSubmitting}
              >
                ✕
              </button>
            )}
          </div>

          {/* Recipe list */}
          {recipesLoading ? (
            <div className="loading-state">
              <p>{t('mealPlan:loadingRecipes')}</p>
            </div>
          ) : filteredRecipes.length === 0 ? (
            <div className="empty-state">
              {searchQuery ? (
                <p>{t('mealPlan:noRecipesFound', { query: searchQuery })}</p>
              ) : (
                <p>{t('mealPlan:noRecipesAvailable')}</p>
              )}
            </div>
          ) : (
            <div className="recipe-picker-list">
              {filteredRecipes.map((recipe) => {
                const isSelected = selectedRecipe?.id === recipe.id;
                const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0);

                return (
                  <div
                    key={recipe.id}
                    className={`recipe-picker-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleRecipeSelect(recipe)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRecipeSelect(recipe);
                      }
                    }}
                    aria-label={`Select ${recipe.title}`}
                  >
                    <div className="recipe-picker-item-content">
                      <h3 className="recipe-picker-item-title">{recipe.title}</h3>
                      {recipe.description && (
                        <p className="recipe-picker-item-description">
                          {recipe.description}
                        </p>
                      )}
                      <div className="recipe-picker-item-meta">
                        {totalTime > 0 && (
                          <span className="meta-item">
                            <span className="meta-icon">⏱</span>
                            {t('recipes:min', { time: totalTime })}
                          </span>
                        )}
                        {recipe.servings && (
                          <span className="meta-item">
                            <span className="meta-icon">🍽</span>
                            {t('recipes:servings', { count: recipe.servings })}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="recipe-picker-item-indicator">✓</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Servings input (shown when recipe selected) */}
          {selectedRecipe && (
            <div className="servings-selector">
              <label htmlFor="servings-input" className="servings-label">
                {t('mealPlan:numberOfServings')}
              </label>
              <input
                id="servings-input"
                type="number"
                className="servings-input"
                value={servings}
                onChange={(e) => setServings(Math.max(1, parseInt(e.target.value) || 1))}
                onFocus={(e) => e.target.select()}
                min="1"
                disabled={isSubmitting}
                aria-label="Number of servings"
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="modal-actions">
            {currentSlot && (
              <button
                className="btn-danger"
                onClick={handleRemoveRecipe}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('mealPlan:removing') : t('mealPlan:removeRecipe')}
              </button>
            )}
            {currentSlot?.recipe_id && (
              <button
                className="btn-secondary"
                onClick={() => {
                  onClose();
                  navigate(`/recipes/${currentSlot.recipe_id}`);
                }}
                disabled={isSubmitting}
              >
                {t('mealPlan:viewRecipe')}
              </button>
            )}
            <div className="modal-actions-right">
              <button
                className="btn-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleAssignRecipe}
                disabled={!selectedRecipe || isSubmitting}
              >
                {isSubmitting
                  ? t('mealPlan:saving')
                  : currentSlot
                  ? t('mealPlan:updateRecipe')
                  : t('mealPlan:addRecipe')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecipePicker;
